
/**
 * Module dependencies.
 */

import { isExternalRoute, isScriptRoute } from 'src/core/utils/routes';
import RouterLink from 'src/components/core/links/router-link';

/**
 * Export `NavElementProps` type.
 */

export type NavElementProps = {
  href?: string | null
};

/**
 * Export `isInternalRoute`.
 *
 * Only internal routes go through `RouterLink`, so only those accept a
 * `locale` prop — passing it to an anchor leaks an invalid DOM attribute.
 */

export function isInternalRoute(href?: string | null): boolean {
  return !!href && !isScriptRoute(href) && !isExternalRoute(href);
}

/**
 * Export `navElementAttrs`.
 *
 * Resolves which element a nav entry renders as: an internal route goes through
 * `RouterLink`, an external url and a `javascript:` link render as plain
 * anchors — script links must not open a new tab — and an entry without `href`
 * renders as a button.
 */

export function navElementAttrs({ href }: NavElementProps) {
  if (!href) {
    return {
      as: 'button',
      type: 'button'
    };
  }

  if (isScriptRoute(href)) {
    return { as: 'a' };
  }

  if (isExternalRoute(href)) {
    return {
      as: 'a',
      rel: 'noopener',
      target: '_blank'
    };
  }

  return { as: RouterLink };
}
